<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { Size } from '../types/size';
import type { Target } from '../types/target';
import { computed, resolveComponent } from 'vue';
import Icon from './Icon.vue';

const props = withDefaults(defineProps<{
	label?: string;
	type?: 'button' | 'submit';
	severity?: 'primary' | 'light' | 'dark' | 'error';
	variant?: 'empty' | 'soft' | 'outline' | 'solid';
	size?: Extract<Size, 'xs' | 'sm' | 'md' | 'lg'>;
	rounded?: 'none' | 'default' | 'full';
	isWide?: boolean;
	isFake?: boolean;
	isActive?: boolean;
	icon?: string;
	iconPosition?: 'start' | 'end';
	to?: RouteLocationRaw;
	target?: Target;
	external?: boolean;
	ariaLabel?: string;
	loading?: boolean;
	disabled?: boolean;
	testId?: string;
}>(), {
	type: 'button',
	severity: 'primary',
	variant: 'empty',
	size: 'md',
	rounded: 'default',
	iconPosition: 'end',
});

defineEmits<{
	click: [MouseEvent];
}>();

const NuxtLink = resolveComponent('NuxtLink');

const element = computed(() => {
	if (props.isFake) { return 'span'; }
	if (props.to) { return NuxtLink; }

	return 'button';
});

const attributes = computed(() => {
	if (props.isFake) { return {}; }

	if (props.to) {
		return {
			to: props.disabled ? undefined : props.to,
			target: props.target,
			external: props.external,
			rel: props.target ? 'noopener noreferrer' : undefined,
		};
	}

	return {
		type: props.type,
		disabled: props.disabled || props.loading,
	};
});
</script>

<template>
	<component
		v-bind="attributes"
		class="btn"
		:class="[{
			[`btn--${props.severity}`]: props.severity,
			[`btn--${props.variant}`]: props.variant,
			[`btn--${props.size}`]: props.size,
			[`btn--rounded-${props.rounded}`]: props.rounded,
			'btn--is-wide': props.isWide,
			'btn--is-fake': props.isFake,
			'btn--is-active': props.isActive,
			'btn--only-icon': !props.label,
			'btn--loading': props.loading,
			'btn--loading-full': props.loading && !props.icon,
			'btn--disabled': props.disabled,
		}]"
		:data-testid="props.testId"
		:is="element"
		@click="!props.isFake && !props.to && $emit('click', $event)"
	>
		<Icon
			:name="props.loading ? 'circle-notch-filled' : props.icon"
			:class="{ btn__loading: props.loading }"
			v-if="props.icon && props.iconPosition === 'start'"
		/>
		<span class="btn__text" v-if="props.label">
			{{ props.label }}
		</span>
		<Icon
			:name="props.loading ? 'circle-notch-filled' : props.icon"
			:class="{ btn__loading: props.loading }"
			v-if="props.icon && props.iconPosition === 'end'"
		/>
		<span class="aria:sr" v-if="props.ariaLabel">{{ props.ariaLabel }}</span>
		<Icon
			name="circle-notch-filled"
			class="btn__loading"
			v-if="props.loading && !props.icon"
		/>
	</component>
</template>

<style scoped lang="scss">
@layer hm-components
{
	.btn
	{
		--btn-border:transparent;
		--btn-radius:0;

		flex-shrink:0;
		@include flex(center, center, var(--btn-gap), true);
		color:var(--btn-c);
		font-size:var(--btn-fs);
		line-height:var(--btn-lh);
		letter-spacing:var(--btn-ls);
		font-weight:var(--btn-fw);
		text-align:center;
		border:1px solid var(--btn-border);
		border-radius:var(--btn-radius);
		background-color:var(--btn-bg);
		box-shadow:none;
		padding:calc(var(--btn-py) - 1px) calc(var(--btn-px) - 1px);
		overflow:hidden;
		position:relative;
		transition:var(--transition-default);

		&:deep(.icon)
		{
			@include size(var(--btn-icon));

			svg path
			{
				vector-effect:non-scaling-stroke;
			}
		}

		@include hover
		{
			transition:var(--transition-default);
		}

		&__text
		{
			@include flex(null, center);
			min-height:var(--btn-icon);
		}
		&__loading
		{
			&:deep(.icon__svg)
			{
				animation:rotate 1s linear infinite;
			}
		}

		// Severity & Variant
		&--primary
		{
			&.btn--empty
			{
				--btn-c:var(--btn-empty-c-primary);

				@include button-interaction(var(--fill-c-light), 4);
			}
			&.btn--soft
			{
				--btn-c:var(--btn-soft-c-primary);
				--btn-bg:var(--btn-soft-bg-primary);

				@include button-interaction(var(--btn-soft-c-primary), 12);
			}
			&.btn--outline
			{
				--btn-c:var(--btn-outline-c-primary);
				--btn-bg:transparent;
				--btn-border:var(--btn-outline-c-primary);

				@include button-outline-interaction(var(--btn-outline-c-primary));
			}
			&.btn--solid
			{
				--btn-c:var(--btn-solid-c-primary);
				--btn-bg:var(--btn-solid-bg-primary);

				@include button-interaction(var(--btn-solid-bg-primary), 92);
			}

			&[data-popper-shown],
			&.btn--is-active
			{
				--btn-c:var(--btn-is-active-c-primary);
				--btn-bg:var(--btn-is-active-bg-primary);
			}
		}
		&--light
		{
			&.btn--empty
			{
				--btn-c:var(--btn-empty-c-light);

				@include button-interaction(var(--btn-empty-c-light), 8);
			}
			&.btn--soft
			{
				--btn-c:var(--btn-soft-c-light);
				--btn-bg:var(--btn-soft-bg-light);

				@include button-interaction(var(--btn-soft-c-light), 12);
			}
			&.btn--outline
			{
				--btn-c:var(--btn-outline-c-light);
				--btn-bg:transparent;
				--btn-border:var(--btn-outline-c-light);

				@include button-outline-interaction(var(--btn-outline-c-light));
			}
			&.btn--solid
			{
				--btn-c:var(--btn-solid-c-light);
				--btn-bg:var(--btn-solid-bg-light);

				@include button-interaction(var(--btn-solid-bg-light), 92);
			}

			&[data-popper-shown],
			&.btn--is-active
			{
				--btn-c:var(--btn-is-active-c-light);
				--btn-bg:var(--btn-is-active-bg-light);
			}
		}
		&--dark
		{
			&.btn--empty
			{
				--btn-c:var(--btn-empty-c-dark);

				@include button-interaction(var(--btn-empty-c-dark), 8);
			}
			&.btn--soft
			{
				--btn-c:var(--btn-soft-c-dark);
				--btn-bg:var(--btn-soft-bg-dark);

				@include button-interaction(var(--btn-soft-c-dark), 12);
			}
			&.btn--outline
			{
				--btn-c:var(--btn-outline-c-dark);
				--btn-bg:transparent;
				--btn-border:var(--btn-outline-c-dark);

				@include button-outline-interaction(var(--btn-outline-c-dark));
			}
			&.btn--solid
			{
				--btn-c:var(--btn-solid-c-dark);
				--btn-bg:var(--btn-solid-bg-dark);

				@include button-interaction(var(--btn-solid-bg-dark), 92);
			}

			&[data-popper-shown],
			&.btn--is-active
			{
				--btn-c:var(--btn-is-active-c-dark);
				--btn-bg:var(--btn-is-active-bg-dark);
			}
		}
		&--error
		{
			&.btn--empty
			{
				--btn-c:var(--btn-empty-c-error);

				@include button-interaction(var(--btn-empty-c-error), 8);
			}
			&.btn--soft
			{
				--btn-c:var(--btn-soft-c-error);
				--btn-bg:var(--btn-soft-bg-error);

				@include button-interaction(var(--btn-soft-c-error), 12);
			}
			&.btn--outline
			{
				--btn-c:var(--btn-outline-c-error);
				--btn-bg:transparent;
				--btn-border:var(--btn-outline-c-error);

				@include button-outline-interaction(var(--btn-outline-c-error));
			}
			&.btn--solid
			{
				--btn-c:var(--btn-solid-c-error);
				--btn-bg:var(--btn-solid-bg-error);

				@include button-interaction(var(--btn-solid-bg-error), 92);
			}

			&[data-popper-shown],
			&.btn--is-active
			{
				--btn-c:var(--btn-is-active-c-error);
				--btn-bg:var(--btn-is-active-bg-error);
			}
		}

		// Size
		&--xs
		{
			--btn-gap:var(--spacing-3xs);
			--btn-fs:1rem;
			--btn-lg:1rem;
			--btn-ls:0.25%;

			--btn-py:var(--spacing-3xs);
			--btn-px:var(--spacing-2xs);
			--btn-icon:1.4rem;

			&.btn--only-icon
			{
				--btn-px:var(--spacing-3xs);
			}
		}
		&--sm
		{
			--btn-gap:var(--spacing-2xs);
			--btn-fs:1.2rem;
			--btn-lh:1.2rem;
			--btn-ls:0.25%;
			--btn-py:var(--spacing-2xs);
			--btn-px:var(--spacing-sm);
			--btn-icon:1.6rem;

			&.btn--only-icon
			{
				--btn-px:var(--spacing-2xs);
			}
		}
		&--md
		{
			--btn-gap:var(--spacing-2xs);
			--btn-fs:1.4rem;
			--btn-lh:1.6rem;
			--btn-ls:0.25%;
			--btn-py:var(--spacing-xs);
			--btn-px:var(--spacing-md);
			--btn-icon:2rem;

			&.btn--only-icon
			{
				--btn-px:var(--spacing-xs);
			}
		}
		&--lg
		{
			--btn-gap:var(--spacing-sm);
			--btn-fs:1.6rem;
			--btn-lh:2rem;
			--btn-ls:0.5%;
			--btn-py:var(--spacing-sm);
			--btn-px:var(--spacing-lg);
			--btn-icon:2.4rem;

			&.btn--only-icon
			{
				--btn-px:var(--spacing-sm);
			}
		}

		// Font Weight
		&--empty
		{
			--btn-fw:400;
		}
		&--soft
		{
			--btn-fw:600;
		}
		&--outline
		{
			--btn-fw:500;
		}
		&--solid
		{
			--btn-fw:600;
		}

		// Rounded
		&--rounded-none
		{
			--btn-radius:0;
		}
		&--rounded-default
		{
			&.btn--xs
			{
				--btn-radius:var(--radius-md);
			}
			&.btn--sm
			{
				--btn-radius:var(--radius-md);
			}
			&.btn--md
			{
				--btn-radius:var(--radius-lg);
			}
			&.btn--lg
			{
				--btn-radius:var(--radius-xl);
			}
		}
		&--rounded-full
		{
			--btn-radius:var(--radius-full);
		}

		// Wide
		&--is-wide
		{
			width:100%;
		}

		// Disabled
		&--disabled
		{
			border-color:transparent;
			cursor:not-allowed;

			@include hover
			{
				border-color:transparent;
			}
			&:focus
			{
				border-color:transparent;
			}

			&.btn--empty
			{
				--btn-c:var(--disabled-c);
				--btn-bg:transparent;
			}
			&.btn--soft,
			&.btn--outline,
			&.btn--solid
			{
				--btn-c:var(--disabled-c);
				--btn-bg:var(--disabled-bg);
			}
		}

		// Loading
		&--loading
		{
			cursor:progress;
		}
		&--loading-full
		{
			.btn__text
			{
				opacity:0;
			}
			.btn__loading
			{
				position:absolute;
				inset:50% auto auto 50%;
				translate:-50% -50%;
				z-index:1;
			}
		}
	}
}
</style>
